import { PageType, SurveyAnswer, SurveyQuestion } from ".."

import { isStorageAvailable, StorageWrapper } from "@utils/infrastructure/storage-util"

const VERSION = 2
const DATA_KEY_PREFIX = "surveyAnswerData"
const EXPIRE_TIME = 4 * 60 * 60 * 1000

interface SurveyDataBase {
    answers: Record<string, SurveyAnswer>
    currentPage: PageType
    slug: string
    currentQuestion: SurveyQuestion
    questionsHash: string
}

interface DataWithMeta {
    data: SurveyDataBase
    expiry: number
}

export class SurveyAnswersStorage {
    static instance: SurveyAnswersStorage
    storage: StorageWrapper | undefined
    key: string

    constructor() {
        this.storage = isStorageAvailable ? new StorageWrapper(window.localStorage) : undefined
        this.key = `${DATA_KEY_PREFIX}:${VERSION}`
    }

    public static getInstance() {
        if (!SurveyAnswersStorage.instance) {
            SurveyAnswersStorage.instance = new SurveyAnswersStorage()
        }
        return SurveyAnswersStorage.instance
    }

    get answers() {
        return this.get()?.answers || []
    }

    get currentQuestion() {
        return this.get()?.currentQuestion
    }

    get slug() {
        return this.get()?.slug
    }

    get currentPage() {
        return this.get()?.currentPage
    }

    get questionsHash() {
        return this.get()?.questionsHash
    }

    setSlug(value: string) {
        this.storage?.storeObject(this.key, {
            data: {
                ...this.get(),
                slug: value,
            },
            expiry: Date.now() + EXPIRE_TIME,
        })
    }

    create(slug: string, question: SurveyQuestion, questionsHash: string) {
        this.storage?.storeObject(this.key, {
            data: {
                answers: {},
                slug: slug,
                question: question,
                currentPage: PageType.CARDS_PAGE,
                questionsHash: questionsHash,
            },
            expiry: Date.now() + EXPIRE_TIME,
        })

        return this.storage?.getObject<DataWithMeta>(this.key)?.data
    }

    get() {
        const date = Date.now()
        const data = this.storage?.getObject<DataWithMeta>(this.key)

        if (!data) return this.create("", {} as SurveyQuestion, "")
        else if (data.expiry < date) {
            this.clean()
            return this.create("", {} as SurveyQuestion, "")
        }

        return data.data
    }

    updateStore(key: string, value: SurveyDataBase[keyof SurveyDataBase]) {
        this.storage?.storeObject(this.key, {
            data: {
                ...this.get(),
                [key]: value,
            },
            expiry: Date.now() + EXPIRE_TIME,
        })
    }

    updateAnswers(answers: Record<string, SurveyAnswer>) {
        this.updateStore("answers", answers)
    }

    clean() {
        this.storage?.remove(this.key)
    }
}
